import PropTypes from "prop-types";
import { Fragment, useMemo } from "react";
import {
  EditIcon,
  PlusIcon,
  PrimaryVerticalDivider,
  StyledLink,
  TrashIcon,
} from "react-common-ui-library";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  BUTTON,
  DESCRIPTION_EN,
  LOGIN_ROUTE,
  LOGIN_VERIFICATION_METHOD_ROUTE,
  MANAGE_VERIFICATION_METHOD,
  MY_PROFILE_ACCOUNT_SETTINGS_ROUTE,
  PROFILE_BASE_ROUTE,
  SECURITY_PREFERENCES_ROUTE,
} from "../../../Constants/CommonConstants";
import {
  ADD_LABEL,
  EDIT_LABEL,
  REMOVE_LABEL,
} from "../../../Constants/LoginVerificationMethodsConstants";
import {
  setIsAddEditVerificationFlow,
  updateAddEditVerificationValues,
} from "../../../Features/LoginSlice";
import { manageLoginSelector } from "../../../Selectors/LoginSelector";
import { StyledVerificationEditRemoveBox } from "../../../Styles/Login/LoginVerificationMethods/LoginVerificationMethodsStyles";
import { manageMyProfileSelector } from "../../../Selectors/MyProfileSelector";
import { manageReferenceDataSelector } from "../../../Selectors/ReferenceDataSelector";

/**
 * @description EditOrRemoveVerification Methods component allows the user to add, edit or remove verification methods.
 * @param {string} phoneEmailValue string value to specify phone or email value.
 * @param {object} verificationMethod string value to specify phone or email verification method.
 * @param {string} setOpenRemoveModal function to set remove verification modal openRemoveModal boolean value.
 * @returns {ReactNode} Renders the add edit and remove functionality.
 */
const EditOrRemoveVerification = ({
  phoneEmailValue,
  verificationMethod,
  setOpenRemoveModal,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const intl = useIntl();

  const location = useLocation();

  const { phoneNo, emailId } = useSelector((state) =>
    manageLoginSelector(state)
  );

  const { emailLanguage } = useSelector((state) =>
    manageMyProfileSelector(state)
  );

  const { englishEmailLanguage, spanishEmailLanguage } = useSelector((state) =>
    manageReferenceDataSelector(state)
  );

  const isProfileRoute = location.pathname?.includes(PROFILE_BASE_ROUTE);

  const navigateRoute = isProfileRoute
    ? `/${MY_PROFILE_ACCOUNT_SETTINGS_ROUTE}/${MANAGE_VERIFICATION_METHOD}`
    : `/${LOGIN_ROUTE}/${MANAGE_VERIFICATION_METHOD}`;

  const onAddButtonClickHandler = () => {
    dispatch(setIsAddEditVerificationFlow());
    navigate(navigateRoute, {
      state: {
        flow: verificationMethod,
        previousScreen: isProfileRoute
          ? SECURITY_PREFERENCES_ROUTE
          : LOGIN_VERIFICATION_METHOD_ROUTE,
        method: "add",
        emailLanguage: englishEmailLanguage,
      },
    });
  };

  const onEditButtonClickHandler = () => {
    dispatch(setIsAddEditVerificationFlow());
    dispatch(updateAddEditVerificationValues());
    navigate(navigateRoute, {
      state: {
        flow: verificationMethod,
        previousScreen: isProfileRoute
          ? SECURITY_PREFERENCES_ROUTE
          : LOGIN_VERIFICATION_METHOD_ROUTE,
        method: "edit",
        emailLanguage:
          emailLanguage === DESCRIPTION_EN
            ? englishEmailLanguage
            : spanishEmailLanguage,
      },
    });
  };

  const showTrashIcon = useMemo(() => {
    if (phoneNo && emailId) {
      return (
        <Fragment>
          <PrimaryVerticalDivider />
          <StyledLink
            component={BUTTON}
            type={BUTTON}
            onClick={() => setOpenRemoveModal(true)}
            id={`${REMOVE_LABEL}-${verificationMethod}`}
          >
            <TrashIcon />
            {intl.formatMessage({ id: REMOVE_LABEL })}
          </StyledLink>
        </Fragment>
      );
    }
    return null;
  }, [emailId, intl, phoneNo, setOpenRemoveModal, verificationMethod]);

  return (
    <StyledVerificationEditRemoveBox>
      {phoneEmailValue ? (
        <Fragment>
          <StyledLink
            component={BUTTON}
            type={BUTTON}
            onClick={onEditButtonClickHandler}
            id={`${EDIT_LABEL}-${verificationMethod}`}
          >
            <EditIcon />
            {intl.formatMessage({ id: EDIT_LABEL })}
          </StyledLink>
          {showTrashIcon}
        </Fragment>
      ) : (
        <StyledLink
          component={BUTTON}
          type={BUTTON}
          onClick={onAddButtonClickHandler}
          id={`${ADD_LABEL}-${verificationMethod}`}
        >
          <PlusIcon />
          {intl.formatMessage({ id: ADD_LABEL })}
        </StyledLink>
      )}
    </StyledVerificationEditRemoveBox>
  );
};

EditOrRemoveVerification.propTypes = {
  phoneEmailValue: PropTypes.string.isRequired,
  verificationMethod: PropTypes.string.isRequired,
  setOpenRemoveModal: PropTypes.func.isRequired,
};

export default EditOrRemoveVerification;
